import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { 
  Search, 
  Filter, 
  X, 
  Calendar as CalendarIcon,
  SlidersHorizontal 
} from "lucide-react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

export interface ContractFilters {
  search: string;
  status: string | null;
  dateRange: DateRange | undefined;
  signerEmail: string;
  dealId: string | null;
}

interface ContractFiltersProps {
  filters: ContractFilters;
  onFiltersChange: (filters: ContractFilters) => void;
  deals?: Array<{ id: string; title: string }>;
}

const statusOptions = [
  { value: "all", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "pending", label: "Pending Approval" },
  { value: "sent", label: "Sent" },
  { value: "viewed", label: "Viewed" },
  { value: "signed", label: "Signed" },
  { value: "expired", label: "Expired" },
];

export function ContractFilters({ filters, onFiltersChange, deals }: ContractFiltersProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const updateFilter = <K extends keyof ContractFilters>(key: K, value: ContractFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      status: null,
      dateRange: undefined,
      signerEmail: "",
      dealId: null,
    });
  };

  const activeFilterCount = [
    filters.status,
    filters.dateRange,
    filters.signerEmail,
    filters.dealId,
  ].filter(Boolean).length;

  return (
    <div className="space-y-4 mb-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contracts..."
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Quick Status Filter */}
        <Select
          value={filters.status || "all"}
          onValueChange={(value) => updateFilter("status", value === "all" ? null : value)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Advanced Filters Popover */}
        <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm">Advanced Filters</h4>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear all
                  </Button>
                )}
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <Label className="text-xs">Date Range</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.dateRange?.from ? (
                        filters.dateRange.to ? (
                          <>
                            {format(filters.dateRange.from, "LLL dd, y")} -{" "}
                            {format(filters.dateRange.to, "LLL dd, y")}
                          </>
                        ) : (
                          format(filters.dateRange.from, "LLL dd, y")
                        )
                      ) : (
                        <span className="text-muted-foreground">Pick a date range</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={filters.dateRange?.from}
                      selected={filters.dateRange}
                      onSelect={(range) => updateFilter("dateRange", range)}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Signer Email */}
              <div className="space-y-2">
                <Label className="text-xs">Signer Email</Label>
                <Input
                  placeholder="Filter by signer email..."
                  value={filters.signerEmail}
                  onChange={(e) => updateFilter("signerEmail", e.target.value)}
                />
              </div>

              {/* Deal Filter */}
              {deals && deals.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs">Associated Deal</Label>
                  <Select
                    value={filters.dealId || "all"}
                    onValueChange={(value) => updateFilter("dealId", value === "all" ? null : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select deal..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Deals</SelectItem>
                      {deals.map((deal) => (
                        <SelectItem key={deal.id} value={deal.id}>
                          {deal.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Clear All */}
        {(filters.search || activeFilterCount > 0) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
            <X className="h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Active Filter Tags */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.status && (
            <Badge variant="secondary" className="gap-1">
              Status: {statusOptions.find(s => s.value === filters.status)?.label}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => updateFilter("status", null)} 
              />
            </Badge>
          )}
          {filters.dateRange && (
            <Badge variant="secondary" className="gap-1">
              Date: {format(filters.dateRange.from!, "MMM d")} - {filters.dateRange.to ? format(filters.dateRange.to, "MMM d") : "..."}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => updateFilter("dateRange", undefined)} 
              />
            </Badge>
          )}
          {filters.signerEmail && (
            <Badge variant="secondary" className="gap-1">
              Signer: {filters.signerEmail}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => updateFilter("signerEmail", "")} 
              />
            </Badge>
          )}
          {filters.dealId && deals && (
            <Badge variant="secondary" className="gap-1">
              Deal: {deals.find(d => d.id === filters.dealId)?.title}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => updateFilter("dealId", null)} 
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
